import { config } from "@/config";
import type { User } from "./types";
import { getCurrentLocale } from "@/i18n/locales";
import { readStorage, writeStorage, removeStorage, StorageUnavailableError } from "@/utils/storage";

const SESSION_KEY = "gameglee.web.session";
const TOKEN_EVENT = "gameglee:tokens";
const AUTH_REVISION_KEY = "gameglee.web.authRevision";
interface Session { id: string; accessToken: string; refreshToken: string; user: User | null }

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string | number,
    readonly retryable = false,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

function session(): Session | null {
  const raw = readStorage(SESSION_KEY);
  if (raw) { try { return JSON.parse(raw) as Session; } catch { return null; } }
  // Migrate the previous release once; remove its independently stored credentials.
  const refreshToken = readStorage("gameglee.web.refresh");
  if (!refreshToken) return null;
  let user: User | null = null;
  try { user = JSON.parse(readStorage("gameglee.web.user") ?? "null") as User | null; } catch { /* no cached profile */ }
  const migrated = { id: crypto.randomUUID(), accessToken: "", refreshToken, user };
  try { writeStorage(SESSION_KEY, JSON.stringify(migrated)); } catch { return null; }
  removeStorage("gameglee.web.refresh");
  removeStorage("gameglee.web.user");
  removeStorage("gameglee.web.access", "session");
  return migrated;
}

let observedSessionId = session()?.id ?? "";
export function getSessionId(): string { return session()?.id ?? ""; }
export function getSessionUser(): User | null { return session()?.user ?? null; }
export function getAccessToken(): string { return session()?.accessToken ?? ""; }
export function getRefreshToken(): string { return session()?.refreshToken ?? ""; }
export function getAuthRevision(): string { return readStorage(AUTH_REVISION_KEY) ?? ""; }
/** Also changes when signing out of an empty session, invalidating pending logins. */
export function advanceAuthRevision(): string {
  const revision = crypto.randomUUID(); writeStorage(AUTH_REVISION_KEY, revision); return revision;
}
function changed(): void {
  observedSessionId = getSessionId();
  window.dispatchEvent(new Event(TOKEN_EVENT));
}
function writeSession(value: Session): void {
  writeStorage(SESSION_KEY, JSON.stringify(value));
  changed();
}
export function saveTokens(tokens: { accessToken: string; refreshToken?: string | null }, user: User | null = null): void {
  advanceAuthRevision();
  writeSession({ id: crypto.randomUUID(), accessToken: tokens.accessToken, refreshToken: tokens.refreshToken ?? "", user });
}
export function updateSessionUser(user: User, expectedSessionId: string): void {
  const current = session();
  if (current?.id !== expectedSessionId) throw sessionChanged();
  writeSession({ ...current, user });
}
export function clearTokens(expectedSessionId?: string): void {
  if (expectedSessionId !== undefined && getSessionId() !== expectedSessionId) return;
  advanceAuthRevision();
  if (!removeStorage(SESSION_KEY)) throw new StorageUnavailableError();
  removeStorage("gameglee.web.refresh");
  removeStorage("gameglee.web.user");
  removeStorage("gameglee.web.access", "session");
  changed();
}
export function onTokensChanged(listener: () => void): () => void {
  const storageListener = (event: StorageEvent) => {
    if (event.key === SESSION_KEY || event.key === null) { observedSessionId = getSessionId(); listener(); }
  };
  window.addEventListener(TOKEN_EVENT, listener);
  window.addEventListener("storage", storageListener);
  return () => { window.removeEventListener(TOKEN_EVENT, listener); window.removeEventListener("storage", storageListener); };
}
export function sessionChanged(): ApiError { return new ApiError("Your account changed in another tab. Please try again.", 409, "SESSION_CHANGED"); }
export function assertSession(expected: string): void {
  if (getSessionId() !== expected) { changed(); throw sessionChanged(); }
}

async function withRefreshLock<T>(operation: (ownsLock: () => boolean) => Promise<T>): Promise<T> {
  if (navigator.locks) return navigator.locks.request("gameglee:refresh", () => operation(() => true));
  // Older engines use an expiring cross-tab lease. A crashed tab cannot strand it.
  const key = "gameglee.web.refreshLock";
  const owner = crypto.randomUUID();
  const deadline = Date.now() + 35000;
  const readLease = (): { owner: string; expires: number } | null => {
    try { return JSON.parse(readStorage(key) ?? "null") as { owner: string; expires: number } | null; } catch { return null; }
  };
  while (Date.now() < deadline) {
    const lease = readLease();
    if (!lease || lease.expires < Date.now()) {
      writeStorage(key, JSON.stringify({ owner, expires: Date.now() + 20000 }));
      // Let simultaneous contenders settle before using a single-use token.
      await new Promise((resolve) => setTimeout(resolve, 40));
      if (readLease()?.owner === owner) {
        const heartbeat = setInterval(() => {
          if (readLease()?.owner === owner) {
            try { writeStorage(key, JSON.stringify({ owner, expires: Date.now() + 20000 })); } catch { /* Ownership is checked again before committing refresh. */ }
          }
        }, 5000);
        try { return await operation(() => readLease()?.owner === owner); }
        finally { clearInterval(heartbeat); if (readLease()?.owner === owner) removeStorage(key); }
      }
    }
    await new Promise((resolve) => setTimeout(resolve, 60));
  }
  throw new ApiError("Sign-in is being refreshed in another tab. Please retry.", 503, "REFRESH_BUSY", true);
}

let refreshPromise: { id: string; promise: Promise<string> } | null = null;
async function refreshAccessToken(expectedId: string, rejectedAccess: string): Promise<string> {
  assertSession(expectedId);
  if (!getRefreshToken()) return "";
  if (refreshPromise?.id === expectedId) return refreshPromise.promise;
  const refresh = async (ownsLock: () => boolean) => {
    assertSession(expectedId);
    const current = session();
    if (!current) return "";
    // A different tab may have refreshed while this request waited for the lock.
    if (current.accessToken && current.accessToken !== rejectedAccess) return current.accessToken;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(`${config.apiBaseUrl}/v1/auth/refresh`, {
        method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ refreshToken: current.refreshToken }), signal: controller.signal,
      });
      if (!ownsLock()) throw new ApiError("Sign-in refresh changed in another tab. Please retry.", 503, "REFRESH_BUSY", true);
      assertSession(expectedId);
      const latest = session()!;
      // Also protect browsers without Web Locks against a late losing response.
      if (latest.refreshToken !== current.refreshToken) return latest.accessToken;
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) clearTokens(expectedId);
        throw await readError(response);
      }
      const body = await response.json() as { accessToken: string; refreshToken?: string; user?: User };
      assertSession(expectedId);
      if (!ownsLock()) throw new ApiError("Sign-in refresh changed in another tab. Please retry.", 503, "REFRESH_BUSY", true);
      const afterRead = session()!;
      if (afterRead.refreshToken !== current.refreshToken) return afterRead.accessToken;
      writeSession({ ...afterRead, accessToken: body.accessToken, refreshToken: body.refreshToken ?? current.refreshToken, user: body.user ?? afterRead.user });
      return body.accessToken;
    } catch (cause) {
      if (controller.signal.aborted) throw new ApiError("Sign-in refresh timed out. Please retry.", 504, "REFRESH_TIMEOUT", true);
      throw cause;
    } finally { clearTimeout(timeout); }
  };
  // Supported browsers serialize refresh across all tabs of this origin.
  const promise = withRefreshLock(refresh)
    .finally(() => { if (refreshPromise?.promise === promise) refreshPromise = null; });
  refreshPromise = { id: expectedId, promise };
  return promise;
}

export interface RequestOptions extends RequestInit {
  auth?: boolean | "optional";
  query?: Record<string, string | number | boolean | null | undefined>;
  retryAuth?: boolean;
  /** Compensation only: successful resources created for an identity that has since changed. */
  onDiscardedResponse?: (body: unknown) => Promise<void>;
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { auth = false, query, retryAuth = true, onDiscardedResponse, ...init } = options;
  const expectedId = observedSessionId;
  if (auth) assertSession(expectedId);
  const authenticated = auth === true || (auth === "optional" && Boolean(getAccessToken() || getRefreshToken()));
  const url = new URL(`${config.apiBaseUrl}${path}`, location.origin);
  Object.entries(query ?? {}).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== "") url.searchParams.set(key, String(value));
  });
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  headers.set("Accept-Language", getCurrentLocale());
  if (init.body && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  let token = "";
  if (authenticated) {
    token = getAccessToken() || await refreshAccessToken(expectedId, "");
    if (!token) throw new ApiError("Sign in to continue.", 401, "AUTHENTICATION_REQUIRED");
    assertSession(expectedId);
    headers.set("Authorization", `Bearer ${token}`);
  }
  async function checkResponseSession(response: Response): Promise<void> {
    if (!authenticated || getSessionId() === expectedId) return;
    if (response.ok && onDiscardedResponse) {
      try { await onDiscardedResponse(await response.json()); } catch { /* preserve the identity-change error */ }
    }
    assertSession(expectedId);
  }
  let response = await fetch(url, { ...init, headers });
  await checkResponseSession(response);
  if (response.status === 401 && authenticated && retryAuth && getRefreshToken()) {
    token = await refreshAccessToken(expectedId, token);
    assertSession(expectedId);
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
      response = await fetch(url, { ...init, headers });
      await checkResponseSession(response);
    }
  }
  if (!response.ok) throw await readError(response);
  if (response.status === 204) return undefined as T;
  const body = await response.json() as T;
  if (authenticated && getSessionId() !== expectedId) {
    if (onDiscardedResponse) { try { await onDiscardedResponse(body); } catch { /* preserve the identity-change error */ } }
    assertSession(expectedId);
  }
  return body;
}

async function readError(response: Response): Promise<ApiError> {
  let body: unknown;
  try { body = await response.json(); } catch { body = undefined; }
  const record = body && typeof body === "object" ? body as Record<string, unknown> : {};
  const nested = record.error && typeof record.error === "object" ? record.error as Record<string, unknown> : {};
  const code = nested.code as string | undefined ?? record.code as string | number | undefined;
  const message = nested.message as string | undefined
    ?? record.detail as string | undefined
    ?? record.message as string | undefined
    ?? `Request failed (${response.status})`;
  return new ApiError(message, response.status, code, response.status >= 500 || response.status === 429);
}

export function jsonBody(value: unknown): Pick<RequestInit, "body" | "headers"> {
  return { body: JSON.stringify(value), headers: { "Content-Type": "application/json" } };
}
